// src/components/AddListingModal.js
import React, { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { createListing, uploadImages } from '../services/api';

/**
 * Upload newly selected files to /api/uploads,
 * returning an array like: [ { filename, relativePath }, ... ]
 */
// async function uploadImages(selectedFiles) {
//   const formData = new FormData();
//   // Just append each file under 'images'
//   selectedFiles.forEach((file) => {
//     formData.append('images', file);
//   });

//   const res = await fetch('https://eurocentrumv1.onrender.com/uploads', {
//     method: 'POST',
//     body: formData,
//   });
//   const data = await res.json();

//   if (!res.ok) {
//     throw new Error(data.error || 'File upload failed');
//   }
//   // => e.g. [ { filename: "pic.jpg", relativePath: "/uploads/pic.jpg" }, ... ]
//   return data.files;
// }

function AddListingModal({ onClose, onRefresh, onAddListing }) {
  const [name, setName] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [customTags, setCustomTags] = useState('');
  const [description, setDescription] = useState('');
  // Price stored as text for consistency with EditListingModal
  const [price, setPrice] = useState('');
  // 1) Location field
  const [location, setLocation] = useState('');
  // New state for rozloha and pocet izieb
  const [rozloha, setRozloha] = useState('');
  const [pocetIzieb, setPocetIzieb] = useState('');
  // Add missing state variables
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Each image: { filename, relativePath, isNew }
  const [images, setImages] = useState([]);
  const [newFiles, setNewFiles] = useState([]); // raw File objects
  const [currentIndex, setCurrentIndex] = useState(0);

  // Available tags
  const availableTags = ['neplatiči', 'auto', 'byt', 'dom', 'pozemok', 'nebytový priestor', 'priemyselná budova', 'prenájom', 'predaj', 'predané', 'prenajaté', 'aktuálne'];

  // Define category tags
  const categoryTags = ['neplatiči', 'auto', 'byt', 'dom', 'pozemok', 'nebytový priestor', 'priemyselná budova'];
  
  // Define sale type tags
  const saleTypeTags = ['predaj', 'prenájom', 'predané', 'prenajaté'];

  // Handle tag selection
  const handleTagToggle = (tag) => {
    setError('');
    
    if (categoryTags.includes(tag)) {
      // If selecting a category tag, remove all other category tags
      const newTags = selectedTags.filter(t => !categoryTags.includes(t));
      setSelectedTags([...newTags, tag]);
    } else {
      // For non-category tags, toggle them normally
      if (selectedTags.includes(tag)) {
        setSelectedTags(selectedTags.filter(t => t !== tag));
      } else {
        setSelectedTags([...selectedTags, tag]);
      }
    }
  };

  // Dropzone
  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp']
    },
    onDrop: (acceptedFiles) => {
      // Clear any existing previews
      setImages([]);
      setNewFiles([]);

      // Create preview URLs and store files
      const newImages = acceptedFiles.map(file => ({
        filename: file.name,
        relativePath: URL.createObjectURL(file),
        isNew: true,
        tempUrl: true  // Mark this as a temporary URL
      }));

      setImages(newImages);
      setNewFiles(acceptedFiles);
    },
  });

  // Prev/Next
  const handlePrev = () => {
    if (images.length === 0) return;
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };
  const handleNext = () => {
    if (images.length === 0) return;
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  // Remove current
  const removeCurrentImage = () => {
    if (images.length === 0) return;
    const removed = images[currentIndex];
    const updated = [...images];
    updated.splice(currentIndex, 1);
    setImages(updated);

    if (removed?.isNew) {
      // remove from newFiles if not uploaded yet
      setNewFiles((prev) => prev.filter((f) => f.name !== removed.filename));
    }

    if (updated.length === 0) {
      setCurrentIndex(0);
    } else {
      setCurrentIndex((prev) => Math.min(prev, updated.length - 1));
    }
  };

  // Cleanup preview URLs when component unmounts
  useEffect(() => {
    return () => {
      images.forEach(img => {
        if (img.tempUrl && img.relativePath) {
          URL.revokeObjectURL(img.relativePath);
        }
      });
    };
  }, [images]);

  // Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    // Validate that at least one category tag is selected
    const hasCategoryTag = selectedTags.some(tag => categoryTags.includes(tag));
    
    if (!hasCategoryTag) {
      setError('Prosím vyberte aspoň jednu kategóriu (auto, byt, dom, pozemok, nebytový priestor, priemyselná budova alebo neplatiči)');
      setIsSubmitting(false);
      return;
    }

    // Check if predaj or prenájom is selected when property type tags are selected
    const hasPropertyTag = selectedTags.some(tag => ['auto', 'byt', 'dom', 'pozemok', 'nebytový priestor', 'priemyselná budova'].includes(tag));
    const hasSaleTypeTag = selectedTags.some(tag => saleTypeTags.includes(tag));
    
    if (hasPropertyTag && !hasSaleTypeTag) {
      setError('Prosím vyberte typ predaja (na predaj, na nájom, predané alebo prenajaté)');
      setIsSubmitting(false);
      return;
    }

    try {
      // Upload images first
      let uploadedImages = [];
      if (newFiles.length > 0) {
        console.log(`Uploading ${newFiles.length} images to Google Cloud Storage...`);
        const response = await uploadImages(newFiles);
        
        if (!response.files || response.files.length === 0) {
          throw new Error('No files were uploaded successfully');
        }
        
        console.log('Successfully uploaded images:', response.files);
        uploadedImages = response.files;
      }

      // Parse custom tags into an array
      const customTagsArray = customTags
        .split(/[,\n]/) // Split by comma OR newline
        .map(tag => tag.trim())
        .filter(tag => tag.length > 0);

      // Create the listing with the uploaded image paths
      const listingData = {
        name,
        tags: selectedTags,
        customTags: customTagsArray,
        description,
        price: selectedTags.includes('neplatiči') ? '' : price,
        location,
        rozloha: rozloha ? Number(rozloha) : 0,
        pocetIzieb: pocetIzieb ? Number(pocetIzieb) : 0,
        images: uploadedImages
      };

      // Create listing
      const newListing = await createListing(listingData);

      // Explicitly set createdAt to a future date on the client side as well (for immediate display)
      newListing.createdAt = new Date(8640000000000000).toISOString(); // Max JS date

      try {
        // Always refresh listings first to get the correct order from server
        if (onRefresh) {
          await onRefresh();
        }
  
        // Then ensure the new listing is at the top through the parent's handler
        if (onAddListing) {
          console.log("Adding new listing to top:", newListing);
          onAddListing(newListing);
        }
      } catch (refreshError) {
        console.error('Error refreshing listings:', refreshError);
        // Continue with closing the modal even if refresh fails
      }

      // Always close modal after successful creation, regardless of refresh status
      if (onClose) onClose();
    } catch (err) {
      setError(err.message);
      console.error('Failed to create listing:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Current image
  const currentImage = images[currentIndex] || null;
  let displaySrc = '';
  if (currentImage?.relativePath) {
    if (currentImage.relativePath.startsWith('http')) {
      displaySrc = currentImage.relativePath;
    } else if (currentImage.tempUrl || currentImage.relativePath.startsWith('blob:')) {
      displaySrc = currentImage.relativePath; // Use blob URL directly for previews
    } else {
      // Use direct path from server - add timestamp to avoid caching
      const timestamp = new Date().getTime();
      displaySrc = `https://eurocentrumv1.onrender.com${currentImage.relativePath.startsWith('/') ? '' : '/'}${currentImage.relativePath}?t=${timestamp}`;
    }
    // Log for debugging
    console.log("AddModal image path:", currentImage.relativePath, "Display src:", displaySrc);
  }

  // Check if neplatiči is selected
  const isNeplatiSelected = selectedTags.includes('neplatiči');

  return (
    <>
      {/* Inlined style block for button styling, same as in EditListingModal */}
      <style>{`
        .modern-button {
          background-color: rgb(44, 105, 160);
          color: #fff;
          border: none;
          padding: 8px 14px;
          border-radius: 5px;
          cursor: pointer;
          font-weight: 500;
          transition: all 0.2s ease;
        }
        .modern-button:hover {
          background-color: rgb(36, 85, 130);
          transform: translateY(-2px);
          box-shadow: 0 4px 8px rgba(0,0,0,0.1);
        }
        .modern-button:focus {
          outline: 2px solid rgb(36, 85, 130);
        }

        .round-button {
          background-color: rgba(0,0,0,0.5);
          color: #fff;
          border: none;
          border-radius: 50%;
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
          font-weight: bold;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        }
        .round-button:hover {
          background-color: rgba(0,0,0,0.7);
          transform: scale(1.05);
        }

        .close-button {
          position: absolute;
          top: 15px;
          right: 15px;
          background-color: rgba(0,0,0,0.5);
          color: white;
          border: none;
          border-radius: 50%;
          width: 34px;
          height: 34px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          font-size: 20px;
          font-weight: bold;
          transition: all 0.3s ease;
          z-index: 10;
        }
        .close-button:hover {
          background-color: rgba(0,0,0,0.7);
          transform: scale(1.1);
        }

        .dropzone:hover {
          background-color: #f0f8ff;
          border-color: rgb(44, 105, 160);
        }
        
        .tag-button {
          padding: 6px 12px;
          margin: 4px;
          border-radius: 16px;
          cursor: pointer;
          border: 1px solid #ccc;
          background-color: #f8f9fa;
          transition: all 0.2s;
        }
        
        .tag-button.selected {
          background-color: rgb(44, 105, 160);
          color: white;
          border-color: rgb(44, 105, 160);
        }
        
        .tag-button.neplatiči.selected {
          background-color: #dc3545;
          border-color: #dc3545;
        }
        
        .tag-button[data-tag="aktuálne"].selected,
        .tag-button[data-tag="aktualne"].selected {
          background-color: #28a745; /* Green color for aktuálne */
          color: white;
          border-color: #28a745;
          font-weight: bold;
        }
        
        .tag-button[data-tag="predané"].selected {
          background-color: #cc0000;
          color: white;
          border-color: #cc0000;
          font-weight: bold;
        }
        
        .tags-container {
          display: flex;
          flex-wrap: wrap;
          margin-bottom: 1rem;
        }
      `}</style>

      <div style={styles.overlay} onClick={onClose}>
        <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
          <h2 style={styles.heading}>Pridať nový inzerát</h2>
          {error && <div style={styles.error}>{error}</div>}
          
          <form onSubmit={handleSubmit} style={styles.form}>
            {/* Left side: image slider */}
            <div style={styles.leftSide}>
              {images.length > 0 && currentImage ? (
                <div style={styles.imageWrapper}>
                  <img
                    src={displaySrc}
                    alt={currentImage.filename}
                    style={styles.previewImage}
                    onError={(e) => {
                      e.target.src = 'data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22600%22%20height%3D%22400%22%20viewBox%3D%220%200%20600%20400%22%20preserveAspectRatio%3D%22none%22%3E%3Cfilter%20id%3D%22b%22%3E%3CfeGaussianBlur%20stdDeviation%3D%2240%22%2F%3E%3C%2Ffilter%3E%3Crect%20width%3D%22600%22%20height%3D%22400%22%20fill%3D%22%23777%22%2F%3E%3Cg%20filter%3D%22url%28%23b%29%22%3E%3Cg%20fill-opacity%3D%22.5%22%3E%3Ccircle%20fill%3D%22%23999%22%20cx%3D%22300%22%20cy%3D%22200%22%20r%3D%22120%22%2F%3E%3C%2Fg%3E%3C%2Fg%3E%3Ctext%20x%3D%22300%22%20y%3D%22210%22%20text-anchor%3D%22middle%22%20font-family%3D%22Arial%2C%20sans-serif%22%20font-size%3D%2224%22%20font-weight%3D%22bold%22%20fill%3D%22%23fff%22%3EImage Not Found%3C%2Ftext%3E%3C%2Fsvg%3E';
                    }}
                  />
                  
                  {/* Navigation controls - keeping the new styled buttons */}
                  <div style={styles.arrowContainer}>
                    <button
                      type="button"
                      className="round-button"
                      onClick={handlePrev}
                      aria-label="Previous image"
                    >
                      ←
                    </button>
                    <button
                      type="button"
                      className="round-button"
                      onClick={handleNext}
                      aria-label="Next image"
                    >
                      →
                    </button>
                  </div>
                  
                  <button
                    type="button"
                    className="round-button"
                    style={styles.removeButton}
                    onClick={removeCurrentImage}
                    aria-label="Remove image"
                  >
                    ✕
                  </button>
                </div>
              ) : (
                <p style={styles.noImagesText}>Žiadne obrázky</p>
              )}
            </div>

            {/* Right side: dropzone & fields */}
            <div style={styles.rightSide}>
              <div
                {...getRootProps({ className: 'dropzone' })}
                style={styles.dropzone}
              >
                <input {...getInputProps()} />
                <p style={styles.dropText}>Kliknite a nahrajte obrázky</p>
              </div>

              {/* Name (Nazov) */}
              <label style={styles.label}>{isNeplatiSelected ? 'Meno' : 'Názov'}</label>
              <input
                style={styles.underlineInput}
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />

              {/* Tags Buttons */}
              <label style={styles.label}>Tagy</label>
              <div className="tags-container">
                {availableTags
                  .filter(tag => {
                    // If neplatiči is selected, hide predaj, prenájom, and aktuálne tags
                    if (selectedTags.includes('neplatiči')) {
                      return !['predaj', 'prenájom', 'aktuálne'].includes(tag);
                    }
                    return true;
                  })
                  .map(tag => (
                    <button
                      key={tag}
                      type="button"
                      className={`tag-button ${tag === 'neplatiči' ? 'neplatiči' : ''} ${selectedTags.includes(tag) ? 'selected' : ''}`}
                      onClick={() => handleTagToggle(tag)}
                      data-tag={tag}
                    >
                      {tag === 'prenájom' ? 'na nájom' : 
                       tag === 'predaj' ? 'na predaj' : tag}
                    </button>
                  ))}
              </div>

              {/* Custom Tags */}
              <div style={styles.formGroup}>
                <label style={styles.labelBlock}>Vlastné značky (oddelené čiarkou alebo novým riadkom)</label>
                <textarea
                  value={customTags}
                  onChange={(e) => setCustomTags(e.target.value)}
                  placeholder="napr. novostavba, zariadený, garáž"
                  style={{...styles.underlineTextarea, minHeight: '60px'}}
                  rows={3}
                />
                <div style={styles.tagInfo}>
                  Vlastné značky sa zobrazia s šedým pozadím.
                </div>
              </div>

              {/* Description (Popis) */}
              <label style={styles.label}>Popis</label>
              <textarea
                style={styles.underlineTextarea}
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />

              {/* Location (Poloha) - only shown if neplatiči not selected */}
              {!isNeplatiSelected && (
                <>
                  <label style={styles.label}>Poloha</label>
                  <input
                    style={styles.underlineInput}
                    type="text"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                  />
                </>
              )}

              {/* Price (Cena) - only shown if neplatiči not selected */}
              {!isNeplatiSelected && (
                <>
                  <label style={styles.label}>Cena</label>
                  <input
                    style={styles.underlineInput}
                    type="text" // store as text
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                  />
                </>
              )}

              {/* Rozloha - hide if auto tag is selected */}
              {!selectedTags.includes('auto') && !isNeplatiSelected && (
                <>
                  <label style={styles.label}>Rozloha (m²)</label>
                  <input
                    style={styles.underlineInput}
                    type="text"
                    value={rozloha}
                    onChange={(e) => setRozloha(e.target.value)}
                  />
                </>
              )}

              {/* Pocet izieb - hide if auto tag is selected */}
              {!selectedTags.includes('auto') && !isNeplatiSelected && (
                <>
                  <label style={styles.label}>Druh nehnuteľnosti</label>
                  <input
                    style={styles.underlineInput}
                    type="text"
                    value={pocetIzieb}
                    onChange={(e) => setPocetIzieb(e.target.value)}
                  />
                </>
              )}

              <div style={styles.buttonRow}>
                <button
                  type="button"
                  className="modern-button"
                  style={{ marginRight: '1rem' }}
                  onClick={onClose}
                  disabled={isSubmitting}
                >
                  Zrušiť
                </button>
                <button
                  type="submit"
                  className="modern-button"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Pridávam...' : 'Pridať'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}

// Styles that match the "EditListingModal"
const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    width: '900px',
    maxWidth: '95%',
    maxHeight: '90vh',
    overflowY: 'auto',
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 8px 20px rgba(0,0,0,0.2)',
    position: 'relative',
  },
  heading: {
    margin: '0 0 1rem 0',
    fontSize: '1.8rem',
    fontWeight: '600',
    color: '#333',
  },
  form: {
    display: 'flex',
    flexDirection: 'row',
    gap: '1rem',
  },
  leftSide: {
    flex: 1,
    border: 'none',
    borderRadius: '6px',
    padding: '1rem',
    marginBottom: '1rem',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '350px',
    boxShadow: 'none',
  },
  imageWrapper: {
    position: 'relative',
    textAlign: 'center',
    width: '320px',
    height: '320px',
    margin: '0 auto',
  },
  previewImage: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    borderRadius: '6px',
  },
  pathText: {
    marginTop: '0.5rem',
    fontSize: '0.85rem',
    color: '#333',
    wordWrap: 'break-word',
    maxWidth: '320px',
    textAlign: 'center',
  },
  removeButton: {
    position: 'absolute',
    top: '8px',
    right: '8px',
    background: 'rgba(255, 255, 255, 0.7)',
    border: 'none',
    borderRadius: '50%',
    width: '30px',
    height: '30px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: '#333',
    fontSize: '16px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
    zIndex: 10,
  },
  arrowContainer: {
    position: 'absolute',
    bottom: '8px',
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0 1rem',
    zIndex: 10,
  },
  arrowButton: {
    background: 'rgba(255, 255, 255, 0.7)',
    border: 'none',
    borderRadius: '50%',
    width: '40px',
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: '#333',
    fontSize: '18px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
  },
  noImagesText: {
    color: '#888',
    fontStyle: 'italic',
  },
  rightSide: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    border: 'none',
  },
  dropzone: {
    border: 'none',
    borderBottom: '2px solid rgb(44, 105, 160)',
    borderRadius: 0,
    padding: '1rem',
    textAlign: 'center',
    marginBottom: '1rem',
    cursor: 'pointer',
    transition: 'border-color 0.2s, background-color 0.2s',
  },
  dropText: {
    margin: 0,
    color: '#666',
    fontSize: '0.95rem',
  },
  label: {
    fontWeight: '400',
    marginBottom: '0.2rem',
    color: '#000',
    fontSize: '0.9rem',
  },
  underlineInput: {
    marginBottom: '1rem',
    padding: '0.3rem 0',
    border: 'none',
    borderRadius: 0,
    borderBottom: '2px solid rgb(44, 105, 160)',
    fontSize: '1rem',
    color: '#333',
    backgroundColor: 'transparent',
    outline: 'none',
    width: '100%',
  },
  underlineTextarea: {
    marginBottom: '1rem',
    padding: '0.3rem 0',
    border: 'none',
    borderRadius: 0,
    borderBottom: '2px solid rgb(44, 105, 160)',
    fontSize: '1rem',
    color: '#333',
    backgroundColor: 'transparent',
    outline: 'none',
    resize: 'vertical',
    whiteSpace: 'pre-wrap',
  },
  buttonRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: 'auto',
  },
  error: {
    color: '#dc3545',
    backgroundColor: '#f8d7da',
    padding: '10px',
    borderRadius: '4px',
    marginBottom: '1rem',
  },
  closeButton: {
    position: 'absolute',
    top: '15px',
    right: '15px',
    background: 'rgba(0, 0, 0, 0.1)',
    border: 'none',
    borderRadius: '50%',
    width: '30px',
    height: '30px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: '#333',
    fontSize: '16px',
    zIndex: 10,
  },
  tagInfo: {
    fontSize: '0.8rem',
    color: '#666',
    marginTop: '0.3rem',
  },
  input: {
    marginBottom: '1rem',
    padding: '0.3rem 0',
    border: 'none',
    borderRadius: 0,
    borderBottom: '2px solid rgb(44, 105, 160)',
    fontSize: '1rem',
    color: '#333',
    backgroundColor: 'transparent',
    outline: 'none',
  },
  formGroup: {
    marginBottom: '1rem',
  },
  tagRow: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  tagButton: {
    padding: '6px 12px',
    margin: '4px',
    borderRadius: '16px',
    cursor: 'pointer',
    border: '1px solid #ccc',
    backgroundColor: '#f8f9fa',
    transition: 'all 0.2s',
  },
  tagButtonSelected: {
    backgroundColor: 'rgb(44, 105, 160)',
    color: 'white',
    borderColor: 'rgb(44, 105, 160)',
  },
  labelBlock: {
    display: 'block',
    marginBottom: '0.2rem',
    color: '#000',
    fontSize: '0.9rem',
  },
};

export default AddListingModal;
